import React, { useEffect, useState } from 'react';
import { Button, Input } from '../../components/ui/Kit';
import CopyButton from '../../components/ui/CopyButton';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { slugify } from '../../lib/pmEventUtils';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiExternalLink } = FiIcons;

const SLUG_FORMAT_HINT =
  'Lowercase letters, numbers, and hyphens only (1–50 chars, no leading/trailing hyphen).';

const SLUG_TAKEN_MESSAGE = 'That link name is already taken for this event, try another';

function normalizeInviteSlugInput(raw) {
  return String(raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, 50);
}

function suggestInviteSlug(baseText, existingSlugs) {
  const taken = new Set((existingSlugs || []).map((s) => String(s || '').toLowerCase()));
  let base = slugify(baseText || 'invite').slice(0, 50);
  if (!base) base = 'invite';
  base = base.replace(/-+$/g, '') || 'invite';
  if (!api.agentPublicPages.isValidSlug(base)) {
    base = (base.replace(/[^a-z0-9-]/g, '').replace(/^-+|-+$/g, '') || 'invite').slice(0, 50);
    if (!api.agentPublicPages.isValidSlug(base)) base = 'invite';
  }
  if (!taken.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const suffix = `-${n}`;
    const candidate = `${base.slice(0, Math.max(1, 50 - suffix.length))}${suffix}`;
    if (api.agentPublicPages.isValidSlug(candidate) && !taken.has(candidate)) return candidate;
  }
  return `${base.slice(0, 40)}-${Date.now().toString(36)}`.slice(0, 50);
}

function InviteUrlRow({ url }) {
  if (!url) return null;
  return (
    <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 min-w-0">
      <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-green-700 flex-shrink-0" />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-green-800 hover:underline truncate"
      >
        {url}
      </a>
      <CopyButton text={url} title="Copy invite URL" className="text-green-700" />
    </div>
  );
}

export default function InviteLinkBlock({ event, userId, invite, onGenerated }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const ownerId = user?.id || userId;
  const [busy, setBusy] = useState(false);
  const [draftSlug, setDraftSlug] = useState('');
  const [slugError, setSlugError] = useState(null);

  const persisted = Boolean(invite?.id && invite?.slug);

  useEffect(() => {
    if (persisted) return;
    let cancelled = false;
    (async () => {
      let base = user?.full_name || user?.email || 'invite';
      try {
        const page = await api.agentPublicPages.getMine();
        if (page?.slug) base = page.slug;
      } catch {
        /* suggestion is optional */
      }
      if (!cancelled) {
        setDraftSlug(suggestInviteSlug(base, []));
        setSlugError(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [persisted, user?.full_name, user?.email]);

  const onDraftSlugChange = (raw) => {
    const next = normalizeInviteSlugInput(raw);
    setDraftSlug(next);
    if (!next) {
      setSlugError('Invitation slug is required');
      return;
    }
    if (!api.agentPublicPages.isValidSlug(next)) {
      setSlugError(SLUG_FORMAT_HINT);
      return;
    }
    setSlugError(null);
  };

  const create = async () => {
    if (!event?.id || !ownerId) return;
    const slug = draftSlug.trim().toLowerCase();
    if (!slug) {
      setSlugError('Invitation slug is required');
      return;
    }
    if (!api.agentPublicPages.isValidSlug(slug)) {
      setSlugError(SLUG_FORMAT_HINT);
      return;
    }

    setBusy(true);
    setSlugError(null);
    try {
      await api.pipelineEvents.createInvitationLink(event.id, ownerId, slug);
      const persistedRow = await api.pipelineEvents.getInvitationLink(event.id, ownerId);
      if (!persistedRow?.id || !persistedRow?.slug) {
        throw new Error('Invite link was not saved. Try again.');
      }
      onGenerated?.(persistedRow);
      addToast({ type: 'success', title: 'Invite link created' });
    } catch (err) {
      const message = err?.message || 'Try again.';
      if (/already used for this event|already taken|duplicate/i.test(message)) {
        setSlugError(SLUG_TAKEN_MESSAGE);
      } else if (/Slug must be|slug is required/i.test(message)) {
        setSlugError(message);
      } else {
        addToast({
          type: 'error',
          title: 'Could not create invite link',
          message,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  if (!persisted) {
    const createDisabled =
      Boolean(slugError)
      || !draftSlug.trim()
      || !api.agentPublicPages.isValidSlug(draftSlug.trim().toLowerCase());

    return (
      <div className="space-y-3 rounded-md border border-lineGray bg-white p-3">
        <Input
          label="Invite slug"
          value={draftSlug}
          onChange={(e) => onDraftSlugChange(e.target.value)}
          error={slugError || undefined}
          placeholder="your-invite-slug"
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
        />
        {!slugError && (
          <p className="text-xs text-slateText">{SLUG_FORMAT_HINT}</p>
        )}
        <Button
          type="button"
          size="sm"
          isLoading={busy}
          disabled={createDisabled || busy || !event?.id || !ownerId}
          onClick={create}
        >
          Create Invite Link
        </Button>
      </div>
    );
  }

  const { primaryUrl, customUrl } = api.pipelineEvents.buildInviteUrls(event, invite);
  const displayUrl = customUrl || primaryUrl;
  if (!displayUrl) return null;
  return <InviteUrlRow url={displayUrl} />;
}
